import {
  FloatingArrow,
  arrow,
  autoUpdate,
  flip,
  offset,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
} from "@floating-ui/react";
import { cross } from "@simplybusiness/icons";
import classNames from "classnames/dedupe";
import type { ReactElement, ReactNode, RefAttributes } from "react";
import { cloneElement, useRef, useState } from "react";
import { useWindowEvent } from "../../hooks";
import type { DOMProps } from "../../types";
import { Button } from "../Button";
import { Icon } from "../Icon";

export type PopoverElementType = HTMLDivElement;

export interface PopoverProps
  extends DOMProps, RefAttributes<PopoverElementType> {
  children?: ReactNode;
  trigger: ReactElement;
  /** Callback that fires each time the accordion is opened */
  onOpen?: () => void;
  /** Callback that fires each time the accordion is closed */
  onClose?: () => void;
  /** Custom class name for setting specific CSS */
  className?: string;
}

const OFFSET_FROM_CONTENT_DEFAULT = 10;

export const Popover = (props: PopoverProps) => {
  const { trigger, children, onOpen, onClose, className } = props;
  const arrowRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      arrow({
        element: arrowRef,
      }),
      offset(OFFSET_FROM_CONTENT_DEFAULT),
      shift(),
      flip(),
    ],
  });
  const dismiss = useDismiss(context, {
    bubbles: true,
    outsidePress: (event: MouseEvent) => {
      // Prevent 'onClose' from firing when clicking the toggle to close
      const toggle = refs.reference.current as HTMLElement;
      const isToggleClick = !toggle?.contains(event.target as HTMLElement);
      if (isToggleClick) {
        onClose?.();
      }
      return true;
    },
  });
  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss]);

  const containerClasses = classNames(
    "mobius",
    "mobius-popover__container",
    className,
  );

  const toggleVisibility = () => {
    if (isOpen) {
      setIsOpen(false);
      onClose?.();
      return;
    }

    setIsOpen(true);
    onOpen?.();
  };

  const triggerComponent = cloneElement(trigger, {
    ref: refs.setReference,
    className: classNames(
      (trigger.props as { className?: string }).className,
      "mobius-popover__toggle",
    ),
    onClick: toggleVisibility,
    ...getReferenceProps(),
  } as Record<string, unknown>);

  useWindowEvent("keydown", e => {
    if (e.key === "Escape") {
      onClose?.();
      e.preventDefault();
      e.stopPropagation();
    }
  });

  return (
    <>
      {triggerComponent}
      {isOpen && (
        <div
          className={containerClasses}
          ref={refs.setFloating}
          style={floatingStyles}
          {...getFloatingProps()}
        >
          <div className="mobius-popover">
            <header className="mobius-popover__header">
              <Button
                type="button"
                className="mobius-popover__close-button"
                onClick={toggleVisibility}
                aria-label="Close"
                variant="ghost"
              >
                <Icon
                  icon={cross}
                  size="md"
                  className="mobius-popover__close-icon"
                />
              </Button>
            </header>
            <div className="mobius-popover__body">{children}</div>
          </div>
          <FloatingArrow
            ref={arrowRef}
            context={context}
            width={20}
            className="mobius-popover__arrow-icon"
          />
        </div>
      )}
    </>
  );
};
